/** Adventure magic: what a hero may cast on the map, and what each spell does.
 *
 * Section 28 gives the gate every cast passes through - spellbook, known spell,
 * mana, valid target, school/mastery - and Section 30 lists the ten spells that
 * act on the map rather than on a battlefield. Both live here, in one place, so
 * `execute.ts` holds a single dispatch rather than ten special cases.
 *
 * Three shapes are load-bearing:
 *
 *  - **Mastery is a lookup, never a branch.** Every table below is indexed
 *    0..3 (none / basic / advanced / expert), so a spell's numbers change with
 *    the hero's school skill without any rule reading a spell id.
 *  - **Movement changes go through `MoveMode`.** Fly and Wave Stride hang a
 *    modifier on the hero; `moveModeOf` in `hero.ts` turns that into the
 *    pathfinder's mode, and `cellEnterable` decides where a crossing may land.
 *    There is no second movement model.
 *  - **Illegality is returned.** Every rule answers with a `Reason` and has
 *    mutated nothing on the way out, so a refused cast costs no mana - which is
 *    what makes it safe for an AI to probe.
 *
 * Where the reference or the spell's own description states a number it is used
 * and cited; where it states a rule but no number ("mastery extends how far
 * away the boat may lie") the table says that its value is modelled.
 */

import type { ExecuteError, Reason } from "../commands.js";
import { illegal } from "../commands.js";
import type { ContentRegistry, SpellDef, SpellSchool } from "../content/registry.js";
import type { ArmyId, HeroId, ModifierSrcId, ObjectId, SpellDefId, TownId } from "../ids.js";
import type { Result } from "../result.js";
import { Err, Ok } from "../result.js";
import { BP_ONE, mulFp, roundHalfAway } from "../numeric.js";
import type { HeroState, MapObjectState, ResourceKind, TownState } from "../state.js";
import { RESOURCES } from "../state.js";
import { cellEnterable, indexOf, isWaterCell, occupantAt, setOccupant } from "../map/create.js";
import { isExplored, revealAll, revealAround } from "../map/fog.js";
import { entityKeys } from "../util/iter.js";
import type { Ctx } from "./ctx.js";
import {
  addResource, mintObjectId, patchHero, patchObject, patchTown, putObject, rollChanceBp,
} from "./ctx.js";
import { asSkillDefId } from "./ids.js";
import {
  SRC_BLOOD_RITUAL, SRC_DIMENSION_DOOR, SRC_DISGUISE_BY_MASTERY, SRC_FLY,
  SRC_SPELL_HARVEST, SRC_METEOR_STRIKE, SRC_TRANSMUTE, SRC_WATER_WALK,
  hasSpellbook, maxMana, maxSpellLevel, moveRulesFor, setSlot, statFor,
} from "./hero.js";
import { spellScalesWithSpecialty } from "../battle/spells.js";
import { removeObject } from "./objects.js";
import { layFootprint } from "./create.js";
import { refreshMemory, revealStep } from "./visibility.js";

// --------------------------------------------------------------- mastery

export type Mastery = 0 | 1 | 2 | 3;

/** The secondary skill that grants mastery in each school (Section 29). */
const SCHOOL_SKILL: Readonly<Record<SpellSchool, string | null>> = Object.freeze({
  air: "air_magic", earth: "earth_magic", fire: "fire_magic", water: "water_magic",
  all: null,
});

const SCHOOLS: readonly SpellSchool[] = Object.freeze(["air", "earth", "fire", "water"]);

/**
 * "Effective mastery is generally the highest applicable school mastery the
 * hero possesses" (Section 29). A spell that belongs to every school - Visions
 * is the adventure example - therefore reads the best of the four; a spell of
 * one school reads only that one.
 */
export function schoolMastery(h: HeroState, school: SpellSchool): Mastery {
  const named = SCHOOL_SKILL[school];
  if (named !== null) return skillOf(h, named);
  let best: Mastery = 0;
  for (const s of SCHOOLS) {
    const lvl = skillOf(h, SCHOOL_SKILL[s]!);
    if (lvl > best) best = lvl;
  }
  return best;
}

function skillOf(h: HeroState, skill: string): Mastery {
  const id = asSkillDefId(skill);
  for (const s of h.secondary) if (s.def === id) return s.level;
  return 0;
}

/**
 * The rung this hero casts this spell at: his school mastery, plus one for the
 * hero specialised in a spell that is a *rule* rather than a number.
 *
 * That rung is the whole of a rule specialty. Gillorn's Call Vessel and
 * Tidecall's Wave Stride carry no magnitude for `spellSpecialtyFp` to multiply -
 * a boat either arrives or it does not - so the specialty is paid where the
 * spell keeps its numbers, in the mastery table it indexes. The combat kernel
 * makes the same trade for Bless and Curse in `masteryWithSpecialty`; both
 * refuse the rung to a hero who does not know the school at all, because a
 * specialty amplifies a skill rather than standing in for one.
 */
export const masteryFor = (
  content: ContentRegistry, h: HeroState, def: SpellDef,
): Mastery => {
  const base = schoolMastery(h, def.school);
  if (base < 1 || base >= 3) return base;
  const hd = content.heroes.get(h.def);
  if (hd === undefined || hd.specialty.kind !== "spell") return base;
  if (hd.specialty.target !== (def.id as string)) return base;
  return spellScalesWithSpecialty(def) ? base : (base + 1) as Mastery;
};

/**
 * Mana price of one cast: the spell's own table indexed by mastery, then every
 * `spellCost` modifier the hero carries. Rounding happens inside `resolveStat`,
 * once (I4).
 */
export function spellCostFor(c: Ctx, h: HeroState, spell: SpellDefId): number {
  const def = c.content.spells.get(spell);
  if (def === undefined) return 0;
  const base = def.cost[masteryFor(c.content, h, def)] ?? def.cost[0] ?? 0;
  const v = statFor(c, h, "spellCost", base).value;
  return v < 0 ? 0 : v;
}

/** The rule name an adventure spell's effect names, or "" for a combat spell. */
export function adventureRuleOf(def: SpellDef): string {
  for (const e of def.effects) if (e.adventure !== undefined) return e.adventure;
  return "";
}

// ------------------------------------------------------------ the tables

/**
 * Blink Step crossings a day, by mastery. The spell's own description is
 * the source: "Two crossings a day at advanced mastery, three at expert."
 */
export const DIMENSION_DOOR_CASTS_PER_DAY: readonly number[] = Object.freeze([1, 1, 2, 3]);

/**
 * Blink Step's reach, in basis points of the map's longer edge.
 *
 * classic scales the jump with the map rather than fixing it in tiles, so the
 * spell is worth the same on a small map as on an extra-large one. The
 * reference states the rule ("map geometry") but no figure, so these are
 * modelled; `DIMENSION_DOOR_MIN_RANGE` keeps the spell usable on a tiny map.
 */
export const DIMENSION_DOOR_RANGE_BP: readonly number[] =
  Object.freeze([2000, 2000, 3500, 5000]);
export const DIMENSION_DOOR_MIN_RANGE = 4;

/** A crossing costs three plain steps of the day's travel. Modelled. */
export const DIMENSION_DOOR_MOVEMENT_COST = 300;

/**
 * Fly and Wave Stride are paid for in movement as well as in mana. The
 * description prices the penalty per step - Wave Stride costs "half again as
 * much at basic mastery, a quarter more at expert" - but step costing belongs
 * to the map subsystem and takes no `MoveMode` term, so the same charge is
 * taken once, as a fixed-point cut of the movement the hero has left when the
 * spell goes off. It is left on the hero as a `movement` modifier as well, so a
 * pool recomputed while the spell is up is cut by exactly the same factor.
 */
export const FLY_MOVEMENT_MULFP: readonly number[] = Object.freeze([500, 500, 667, 1000]);
export const WATER_WALK_MOVEMENT_MULFP: readonly number[] = Object.freeze([667, 667, 800, 800]);

/** Wind Sight / Earth Sight reach, in tiles; -1 is "across the whole map" at expert. */
export const VIEW_RANGE_BY_MASTERY: readonly number[] = Object.freeze([8, 12, 20, -1]);
/** Visions reach, in tiles. Modelled; the description states only "mastery widens". */
export const VISIONS_RANGE_BY_MASTERY: readonly number[] = Object.freeze([3, 5, 8, 12]);
/** Sink Vessel reach, in tiles. Modelled, same reason. */
export const SCUTTLE_BOAT_RANGE_BY_MASTERY: readonly number[] = Object.freeze([1, 3, 5, 8]);

/**
 * Call Vessel conjures a *new* hull only when the caster owns no idle one, and
 * that is the roll the description means by "at expert it never fails".
 */
export const SUMMON_BOAT_SUCCESS_BP: readonly number[] =
  Object.freeze([2500, 5000, 7500, BP_ONE]);

/**
 * Recall's reach, in tiles: how far away a hero of your own may be standing and
 * still be pulled to you. Modelled, and deliberately shorter than Dimension
 * Door's - it moves somebody else, and a kingdom-wide version would make every
 * garrison a staging post.
 */
export const RECALL_RANGE_BY_MASTERY: readonly number[] = Object.freeze([6, 10, 16, 24]);
/** Scry's reach, in tiles; -1 is "every enemy hero on the map" at expert. */
export const SCRY_RANGE_BY_MASTERY: readonly number[] = Object.freeze([8, 12, 20, -1]);
/** Blood Rite: creatures offered, by mastery, and spell points per tier. */
export const BLOOD_RITUAL_CREATURES: readonly number[] = Object.freeze([1, 1, 2, 3]);
export const BLOOD_RITUAL_MANA_PER_TIER = 3;
/** Harvest: wood and ore raised, by mastery, and the rare resource at expert. */
export const HARVEST_BY_MASTERY: readonly number[] = Object.freeze([1, 1, 2, 3]);
export const HARVEST_RARE_AT_EXPERT = 1;
/** Transmute: units taken, and the gold each is worth, by mastery. */
export const TRANSMUTE_UNITS = 5;
export const TRANSMUTE_GOLD_PER_UNIT: readonly number[] = Object.freeze([80, 80, 120, 180]);
/** Meteor Strike: reach in tiles, and hit points of guard it destroys per power. */
export const METEOR_RANGE_BY_MASTERY: readonly number[] = Object.freeze([3, 5, 8, 12]);
export const METEOR_HP_PER_POWER: readonly number[] = Object.freeze([10, 10, 15, 25]);
/** Tunnel costs the day's travel, like any other way between the planes. */
export const TUNNEL_MOVEMENT_COST = 300;

/** What Earth Sight shows: the economy. */
const VIEW_EARTH_KINDS: ReadonlySet<string> = new Set(["mine", "resource"]);
/** What Wind Sight shows: everything else worth walking to. */
const VIEW_AIR_KINDS: ReadonlySet<string> = new Set(
  ["artifact", "treasure", "bank", "bonus", "dwelling", "shrine", "scholar", "keymaster"],
);

// ------------------------------------------------------------- geometry

/** The map is eight-connected, so "distance" is the Chebyshev step count. */
export const tileDistance = (
  ax: number, ay: number, bx: number, by: number,
): number => Math.max(Math.abs(ax - bx), Math.abs(ay - by));

/** Another plane is further than anything on this one, but is still ordered. */
const OTHER_PLANE = 1_000_000;

export function dimensionDoorRange(c: Ctx, mastery: Mastery): number {
  const edge = Math.max(c.s.map.width, c.s.map.height);
  const bp = DIMENSION_DOOR_RANGE_BP[mastery] ?? DIMENSION_DOOR_RANGE_BP[0]!;
  const r = roundHalfAway((edge * bp) / BP_ONE);
  return r < DIMENSION_DOOR_MIN_RANGE ? DIMENSION_DOOR_MIN_RANGE : r;
}

/**
 * How many crossings this hero has already made today.
 *
 * The tally is one zero-valued `movement` modifier per cast rather than a
 * counter on `HeroState`: a turn-scoped modifier is already cleared for us at
 * the day boundary, and adding zero to movement is a no-op by construction.
 */
export function dimensionDoorCastsToday(h: HeroState): number {
  let n = 0;
  for (const m of h.modifiers) if (m.source === SRC_DIMENSION_DOOR) n++;
  return n;
}

// ------------------------------------------------------------- the gate

/**
 * Everything a cast needs that does not depend on where it is aimed: the book,
 * the spell, Wisdom, and the mana. Split out so a command enumerator or a
 * spellbook screen can grey a spell out without inventing a target for it.
 */
export function castGate(c: Ctx, h: HeroState, spell: SpellDefId): Reason | null {
  if (!hasSpellbook(h)) return { code: "NO_SPELLBOOK" };
  if (!h.spellbook.includes(spell)) return { code: "SPELL_NOT_KNOWN", spell };
  const def = c.content.spells.get(spell);
  if (def === undefined) return { code: "NO_SUCH_ENTITY", id: spell as string };
  if (!def.adventure) return { code: "TARGET_INVALID" };
  // Wisdom caps what may be *cast* as well as what may be learned, so a spell
  // picked up from a shrine before the skill stays unusable until it arrives.
  if (def.level > maxSpellLevel(h)) return { code: "TARGET_INVALID" };
  const cost = spellCostFor(c, h, spell);
  if (h.mana < cost) return { code: "OUT_OF_MANA", have: h.mana, need: cost };
  return null;
}

/** Cast one adventure spell. The mana is spent only once the rule has said yes. */
export function castAdventureSpell(
  c: Ctx, heroId: HeroId, spell: SpellDefId, x: number, y: number, z: number,
): Result<true, ExecuteError> {
  const h = c.s.heroes[heroId];
  if (h === undefined) return Err(illegal({ code: "NO_SUCH_ENTITY", id: heroId as string }));
  const gated = castGate(c, h, spell);
  if (gated !== null) return Err(illegal(gated));

  const def = c.content.spells.get(spell)!;
  const cost = spellCostFor(c, h, spell);
  const bad = applyRule(c, heroId, adventureRuleOf(def), masteryFor(c.content, h, def), x, y, z);
  if (bad !== null) return Err(illegal(bad));

  const after = c.s.heroes[heroId]!;
  patchHero(c, heroId, { mana: after.mana < cost ? 0 : after.mana - cost });
  c.events.push({ type: "Message", player: h.owner, text: `cast:${spell as string}` });
  return Ok(true);
}

function applyRule(
  c: Ctx, heroId: HeroId, rule: string, mastery: Mastery,
  x: number, y: number, z: number,
): Reason | null {
  switch (rule) {
    case "adventure.blink_step": return dimensionDoor(c, heroId, mastery, x, y, z);
    case "adventure.homeward_gate": return townPortal(c, heroId, mastery, x, y, z);
    case "adventure.fly": return travelMode(c, heroId, mastery, "fly");
    case "adventure.wave_stride": return travelMode(c, heroId, mastery, "waterWalk");
    case "adventure.call_vessel": return summonBoat(c, heroId, mastery);
    case "adventure.sink_vessel": return scuttleBoat(c, heroId, mastery, x, y, z);
    case "adventure.wind_sight": return view(c, heroId, mastery, VIEW_AIR_KINDS);
    case "adventure.earth_sight": return view(c, heroId, mastery, VIEW_EARTH_KINDS);
    case "adventure.visions": return visions(c, heroId, mastery);
    case "adventure.disguise": return disguise(c, heroId, mastery);
    case "adventure.recall": return recall(c, heroId, mastery);
    case "adventure.scry": return scry(c, heroId, mastery);
    case "adventure.blood_rite": return bloodRitual(c, heroId, mastery);
    case "adventure.harvest": return harvest(c, heroId, mastery);
    case "adventure.transmute": return transmute(c, heroId, mastery);
    case "adventure.meteor_strike": return meteorStrike(c, heroId, mastery, x, y, z);
    case "adventure.tunnel": return tunnel(c, heroId, mastery);
    default: return { code: "NOT_IMPLEMENTED", what: rule === "" ? "adventure spell" : rule };
  }
}

/**
 * Every target the command enumerator can name for one spell, or none.
 *
 * The contract in `legal.ts` is that a concrete command it returns executes
 * `Ok`, so this has to agree with the rules below exactly - which is why it
 * lives here beside them rather than in the enumerator. Blink Step and
 * Sink Vessel return nothing: their target is one cell out of tens of
 * thousands, and that is the caller's to choose, the same split `MOVE_HERO`
 * already uses.
 */
export function enumerableTargets(
  c: Ctx, heroId: HeroId, spell: SpellDefId,
): readonly (readonly [number, number, number])[] {
  const h = c.s.heroes[heroId];
  if (h === undefined) return [];
  const def = c.content.spells.get(spell);
  if (def === undefined || castGate(c, h, spell) !== null) return [];
  const here: readonly [number, number, number] = [h.x, h.y, h.z];

  switch (adventureRuleOf(def)) {
    case "adventure.wind_sight":
    case "adventure.earth_sight":
    case "adventure.visions":
    case "adventure.disguise":
      return [here];
    // Scry is the one of the five that can have nothing to look at, and the
    // rule refuses when it does - so the enumerator must refuse it too.
    case "adventure.scry":
      return scryTargets(c, h, masteryFor(c.content, h, def)).length === 0 ? [] : [here];
    // The once-a-day spells light their own cell only while the day's cast is
    // still owed, so a UI greys them out rather than offering a refusal.
    case "adventure.harvest":
      return castToday(h, SRC_SPELL_HARVEST) ? [] : [here];
    case "adventure.transmute":
      return castToday(h, SRC_TRANSMUTE) || transmuteSource(c, h) === null ? [] : [here];
    case "adventure.blood_rite":
      return castToday(h, SRC_BLOOD_RITUAL) || bloodRitualSlot(c, h, masteryFor(c.content, h, def)) === null
        ? [] : [here];
    case "adventure.recall":
      return recallTarget(c, h, masteryFor(c.content, h, def)) === null ? [] : [here];
    case "adventure.tunnel":
      return tunnelTarget(c, h) === null ? [] : [here];
    case "adventure.meteor_strike": {
      if (castToday(h, SRC_METEOR_STRIKE)) return [];
      const mastery = masteryFor(c.content, h, def);
      const range = METEOR_RANGE_BY_MASTERY[mastery] ?? METEOR_RANGE_BY_MASTERY[0]!;
      const out: (readonly [number, number, number])[] = [];
      for (const id of entityKeys(c.s.objects)) {
        const o = c.s.objects[id as ObjectId]!;
        if (o.removed || o.z !== h.z || o.guard === null) continue;
        if (tileDistance(h.x, h.y, o.x, o.y) > range) continue;
        // The strike has to be worth making, and that is the rule's own answer.
        if (!meteorStrikeTarget(c, h, mastery, o.x, o.y, o.z).ok) continue;
        out.push([o.x, o.y, o.z]);
      }
      return out;
    }
    case "adventure.fly":
      return h.boat === null && !h.modifiers.some((m) => m.source === SRC_FLY) ? [here] : [];
    case "adventure.wave_stride":
      return h.boat === null && !h.modifiers.some((m) => m.source === SRC_WATER_WALK)
        ? [here] : [];
    case "adventure.call_vessel":
      return h.boat === null && nearestFreeWater(c, h) !== null ? [here] : [];
    case "adventure.homeward_gate": {
      if (h.boat !== null || h.movementLeft <= 0) return [];
      const towns = landableTowns(c, heroId);
      if (towns.length === 0) return [];
      if (masteryFor(c.content, h, def) >= 3) return towns.map((t) => [t.x, t.y, t.z] as const);
      let nearest = towns[0]!;
      for (const t of towns) if (townDistance(h, t) < townDistance(h, nearest)) nearest = t;
      return [[nearest.x, nearest.y, nearest.z]];
    }
    default:
      return [];
  }
}

// ---------------------------------------------------------- aiming a spell

/**
 * How a spell is aimed, in the only three shapes the map spells use.
 *
 *  - `self`   - it goes off where the hero stands; there is nothing to pick.
 *  - `town`   - the player picks one of a listed set of towns.
 *  - `cell`   - the player picks any cell inside a reach.
 *
 * `none` is a spell that is not an adventure spell at all.
 */
export type AdventureTargetShape = "none" | "self" | "town" | "cell";

export type AdventureTargetCell = readonly [number, number, number];

/**
 * Everything a targeting cursor needs, and nothing a rule needs.
 *
 * `cells` is the whole legal set, so a UI can light the map without asking the
 * kernel about each cell in turn, and every entry in it is a cast the kernel
 * will accept - `adventureTargeting` calls the same target tests the cast
 * calls, which is what stops the highlight and the rule drifting apart (I10).
 * `reason` is why the set is empty when it is: the gate (no mana, no book, the
 * day's crossings spent), or `TARGET_INVALID` when the gate is open and there
 * is simply nothing in reach. A UI that greys a spell out owes the player that
 * sentence.
 *
 * Blink Step on an extra-large map at expert reaches 72 cells, so this
 * walks about twenty thousand of them once, when the cursor is armed. It is
 * deliberately not called per hover.
 */
export interface AdventureTargeting {
  readonly shape: AdventureTargetShape;
  readonly cells: readonly AdventureTargetCell[];
  /** Chebyshev reach for `cell`, so a UI can draw the ring. 0 otherwise. */
  readonly range: number;
  readonly reason: Reason | null;
}

const NOTHING: AdventureTargeting = Object.freeze({
  shape: "none", cells: Object.freeze([]), range: 0,
  reason: Object.freeze({ code: "TARGET_INVALID" }) as Reason,
});

/** The aiming shape of a spell, from its adventure rule alone. */
export function adventureTargetShape(def: SpellDef): AdventureTargetShape {
  switch (adventureRuleOf(def)) {
    case "": return "none";
    case "adventure.blink_step":
    case "adventure.meteor_strike":
    case "adventure.sink_vessel": return "cell";
    case "adventure.homeward_gate": return "town";
    default: return "self";
  }
}

export function adventureTargeting(
  c: Ctx, heroId: HeroId, spell: SpellDefId,
): AdventureTargeting {
  const h = c.s.heroes[heroId];
  if (h === undefined) {
    return { ...NOTHING, reason: { code: "NO_SUCH_ENTITY", id: heroId as string } };
  }
  const def = c.content.spells.get(spell);
  if (def === undefined) {
    return { ...NOTHING, reason: { code: "NO_SUCH_ENTITY", id: spell as string } };
  }
  const shape = adventureTargetShape(def);
  if (shape === "none") return NOTHING;

  const gated = castGate(c, h, spell);
  if (gated !== null) return { shape, cells: [], range: 0, reason: gated };

  const mastery = masteryFor(c.content, h, def);
  if (adventureRuleOf(def) === "adventure.blink_step") {
    const range = dimensionDoorRange(c, mastery);
    const gate = dimensionDoorGate(c, h, mastery);
    if (gate !== null) return { shape, cells: [], range, reason: gate };
    const cells: AdventureTargetCell[] = [];
    // Clamped to the map: `dimensionDoorTarget` refuses an off-map cell anyway,
    // but on an extra-large map at expert this square is twenty thousand cells
    // and a caster near an edge would spend most of the walk outside it.
    const y0 = Math.max(0, h.y - range), y1 = Math.min(c.s.map.height - 1, h.y + range);
    const x0 = Math.max(0, h.x - range), x1 = Math.min(c.s.map.width - 1, h.x + range);
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        if (dimensionDoorTarget(c, h, mastery, x, y, h.z) === null) cells.push([x, y, h.z]);
      }
    }
    return { shape, cells, range, reason: cells.length === 0 ? { code: "TARGET_INVALID" } : null };
  }

  if (adventureRuleOf(def) === "adventure.sink_vessel") {
    const range = SCUTTLE_BOAT_RANGE_BY_MASTERY[mastery] ?? SCUTTLE_BOAT_RANGE_BY_MASTERY[0]!;
    // Boats, not cells: the reach is a handful of tiles and there are never
    // many hulls, so the object table is the cheaper side to walk.
    const cells: AdventureTargetCell[] = [];
    for (const id of entityKeys(c.s.objects)) {
      const o = c.s.objects[id as ObjectId]!;
      if (o.removed || o.kind !== "boat") continue;
      if (scuttleBoatTarget(c, h, mastery, o.x, o.y, o.z).ok) cells.push([o.x, o.y, o.z]);
    }
    return { shape, cells, range, reason: cells.length === 0 ? { code: "TARGET_INVALID" } : null };
  }

  const cells = enumerableTargets(c, heroId, spell);
  return { shape, cells, range: 0, reason: cells.length === 0 ? { code: "TARGET_INVALID" } : null };
}

// ------------------------------------------------------- dimension door

/**
 * Everything Blink Step refuses before a target is even named: the day's
 * allowance and the movement the crossing costs.
 *
 * Split from the target test because a UI that arms a targeting cursor has to
 * distinguish "you may not cast this at all today" - which is a refusal to
 * report before the cursor appears - from "not that cell", which is a refusal
 * to report when the player clicks one. Both halves are still the only
 * implementation of the rule: `dimensionDoor` below is the two of them in
 * order, so the cells the enumerator lights are exactly the cells the cast
 * accepts (I10).
 */
export function dimensionDoorGate(c: Ctx, h: HeroState, mastery: Mastery): Reason | null {
  const allowed = DIMENSION_DOOR_CASTS_PER_DAY[mastery] ?? 1;
  // The command vocabulary has no CAST_LIMIT_REACHED; this is its nearest
  // structured reason for "the day's allowance is used up".
  if (dimensionDoorCastsToday(h) >= allowed) return { code: "ALREADY_CAST_THIS_ROUND" };
  if (h.movementLeft < DIMENSION_DOOR_MOVEMENT_COST) {
    return {
      code: "INSUFFICIENT_MOVEMENT",
      have: h.movementLeft, need: DIMENSION_DOOR_MOVEMENT_COST,
    };
  }
  return null;
}

/** Whether one cell is a legal landing for this hero's crossing. Pure. */
export function dimensionDoorTarget(
  c: Ctx, h: HeroState, mastery: Mastery, x: number, y: number, z: number,
): Reason | null {
  // A door opens on the plane it was opened from; the underground is a separate
  // map and is reached by walking down to it.
  if (z !== h.z) return { code: "TARGET_INVALID" };

  const max = dimensionDoorRange(c, mastery);
  const distance = tileDistance(h.x, h.y, x, y);
  if (distance === 0) return { code: "TARGET_INVALID" };
  if (distance > max) return { code: "OUT_OF_RANGE", distance, max };

  const i = indexOf(c.s.map, x, y, z);
  if (i < 0) return { code: "TARGET_INVALID" };
  const fog = c.s.fog[h.owner];
  if (fog === undefined || !isExplored(fog, i)) return { code: "TARGET_INVALID" };
  // The hero's own MoveMode decides where they can stand, so a hero under Water
  // Walk may cross onto water and a land hero may not.
  if (!cellEnterable(c.s.map, i, moveRulesFor(c, h))) return { code: "TARGET_INVALID" };
  // The expansion's guard fix (Section 30): a crossing may not land on an occupied cell,
  // which is what stops Blink Step skipping a guarded trigger.
  if (occupantAt(c.s.map, i) !== null) return { code: "TARGET_INVALID" };
  if (heroAt(c, x, y, z) !== null) return { code: "TARGET_INVALID" };
  return null;
}

function dimensionDoor(
  c: Ctx, heroId: HeroId, mastery: Mastery, x: number, y: number, z: number,
): Reason | null {
  const h = c.s.heroes[heroId]!;
  const gated = dimensionDoorGate(c, h, mastery);
  if (gated !== null) return gated;
  const bad = dimensionDoorTarget(c, h, mastery, x, y, z);
  if (bad !== null) return bad;

  leaveTown(c, heroId);
  patchHero(c, heroId, {
    x, y, z, inTown: null,
    movementLeft: h.movementLeft - DIMENSION_DOOR_MOVEMENT_COST,
    modifiers: [...h.modifiers, {
      source: SRC_DIMENSION_DOOR, stat: "movement", op: "add", value: 0,
      expires: { kind: "turns", turns: 1 },
    }],
  });
  revealStep(c, c.s.heroes[heroId]!);
  return null;
}

// ----------------------------------------------------------- town portal

function townPortal(
  c: Ctx, heroId: HeroId, mastery: Mastery, x: number, y: number, z: number,
): Reason | null {
  const h = c.s.heroes[heroId]!;
  if (h.boat !== null) return { code: "TARGET_INVALID" };
  if (h.movementLeft <= 0) return { code: "INSUFFICIENT_MOVEMENT", have: 0, need: 1 };

  const towns = landableTowns(c, heroId);
  if (towns.length === 0) return { code: "TARGET_INVALID" };

  let nearest = towns[0]!;
  let nearestD = townDistance(h, nearest);
  for (const t of towns) {
    const d = townDistance(h, t);
    if (d < nearestD) { nearestD = d; nearest = t; }
  }

  // Basic and advanced reach only the nearest free town; "expert mastery lets
  // you choose any town you own" (spells.json).
  let target = nearest;
  if (mastery >= 3) {
    let picked: TownState | null = null;
    for (const t of towns) if (t.x === x && t.y === y && t.z === z) picked = t;
    if (picked === null) return { code: "TARGET_INVALID" };
    target = picked;
  } else if (x !== nearest.x || y !== nearest.y || z !== nearest.z) {
    return { code: "OUT_OF_RANGE", distance: tileDistance(h.x, h.y, x, y), max: nearestD };
  }

  leaveTown(c, heroId);
  // The portal is a day's travel: the hero arrives with nothing left to spend,
  // which is what stops it being a free repositioning tool every morning.
  patchHero(c, heroId, {
    x: target.x, y: target.y, z: target.z, inTown: target.id, movementLeft: 0,
  });
  patchTown(c, target.id, { visitingHero: heroId });
  revealStep(c, c.s.heroes[heroId]!);
  return null;
}

/**
 * Towns the caster may land in: owned, and with nobody standing in them - "the
 * nearest town without a hero in it" excludes the one the caster is in, so the
 * spell never resolves to a cast that moves nothing.
 */
export function landableTowns(c: Ctx, heroId: HeroId): readonly TownState[] {
  const h = c.s.heroes[heroId];
  if (h === undefined) return [];
  const out: TownState[] = [];
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id as TownId]!;
    if (t.owner !== h.owner || t.visitingHero !== null) continue;
    out.push(t);
  }
  return out;
}

const townDistance = (h: HeroState, t: TownState): number =>
  tileDistance(h.x, h.y, t.x, t.y) + (t.z === h.z ? 0 : OTHER_PLANE);

// ------------------------------------------------------ fly / water walk

function travelMode(
  c: Ctx, heroId: HeroId, mastery: Mastery, mode: "fly" | "waterWalk",
): Reason | null {
  const h = c.s.heroes[heroId]!;
  // Both spells rewrite how the hero walks; neither means anything at sea.
  if (h.boat !== null) return { code: "TARGET_INVALID" };
  const src = mode === "fly" ? SRC_FLY : SRC_WATER_WALK;
  if (h.modifiers.some((m) => m.source === src)) return { code: "TARGET_INVALID" };
  const table = mode === "fly" ? FLY_MOVEMENT_MULFP : WATER_WALK_MOVEMENT_MULFP;
  const factor = table[mastery] ?? table[0]!;

  patchHero(c, heroId, {
    movementLeft: mulFp(h.movementLeft, factor),
    modifiers: [...h.modifiers, {
      source: src, stat: "movement", op: "mulFp", value: factor,
      stackGroup: "spell.travel", stackRule: "highest",
      expires: { kind: "turns", turns: 1 },
    }],
  });
  return null;
}

// ------------------------------------------------------------- the boats

function summonBoat(c: Ctx, heroId: HeroId, mastery: Mastery): Reason | null {
  const h = c.s.heroes[heroId]!;
  if (h.boat !== null) return { code: "TARGET_INVALID" };
  const shore = nearestFreeWater(c, h);
  if (shore === null) return { code: "TARGET_INVALID" };

  const idle = nearestIdleBoat(c, heroId);
  if (idle !== null) {
    moveObject(c, idle, shore[0], shore[1], shore[2]);
    return null;
  }
  const bp = SUMMON_BOAT_SUCCESS_BP[mastery] ?? SUMMON_BOAT_SUCCESS_BP[0]!;
  if (!rollChanceBp(c, "map.encounter", bp)) {
    c.events.push({ type: "Message", player: h.owner, text: "call_vessel_failed" });
    return null;
  }
  const def = boatDef(c.content);
  if (def === null) return { code: "NOT_IMPLEMENTED", what: "boat" };
  const o: MapObjectState = {
    id: mintObjectId(c), kind: "boat", def,
    x: shore[0], y: shore[1], z: shore[2],
    owner: h.owner, payload: {}, guard: null, visitedBy: [], removed: false,
  };
  putObject(c, o);
  layFootprint(c, o);
  return null;
}

/**
 * The hull one cell names, or the reason there is none to sink there.
 *
 * Separated from the cast for the same reason Blink Step's target test is:
 * a targeting cursor has to be able to ask "would this cell be accepted?"
 * without casting, and the answer must come from the rule rather than from a
 * second copy of it in the UI.
 */
export function scuttleBoatTarget(
  c: Ctx, h: HeroState, mastery: Mastery, x: number, y: number, z: number,
): Result<ObjectId, Reason> {
  if (z !== h.z) return Err({ code: "TARGET_INVALID" });
  const max = SCUTTLE_BOAT_RANGE_BY_MASTERY[mastery] ?? SCUTTLE_BOAT_RANGE_BY_MASTERY[0]!;
  const distance = tileDistance(h.x, h.y, x, y);
  if (distance > max) return Err({ code: "OUT_OF_RANGE", distance, max });

  const i = indexOf(c.s.map, x, y, z);
  if (i < 0) return Err({ code: "TARGET_INVALID" });
  const occ = occupantAt(c.s.map, i);
  if (occ === null) return Err({ code: "TARGET_INVALID" });
  const o = c.s.objects[occ];
  if (o === undefined || o.removed || o.kind !== "boat") return Err({ code: "TARGET_INVALID" });
  // Only an empty hull: scuttling the boat under a hero would drown them.
  if (boatOccupied(c, occ)) return Err({ code: "TARGET_INVALID" });
  return Ok(occ);
}

function scuttleBoat(
  c: Ctx, heroId: HeroId, mastery: Mastery, x: number, y: number, z: number,
): Reason | null {
  const h = c.s.heroes[heroId]!;
  const found = scuttleBoatTarget(c, h, mastery, x, y, z);
  if (!found.ok) return found.error;
  removeObject(c, found.value);
  return null;
}

function boatOccupied(c: Ctx, boat: ObjectId): boolean {
  for (const id of entityKeys(c.s.heroes)) if (c.s.heroes[id as HeroId]!.boat === boat) return true;
  return false;
}

/** The free water cell beside the hero with the lowest index, or null. */
function nearestFreeWater(c: Ctx, h: HeroState): readonly [number, number, number] | null {
  const map = c.s.map;
  let best: readonly [number, number, number] | null = null;
  let bestIndex = Number.MAX_SAFE_INTEGER;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const x = h.x + dx, y = h.y + dy;
      const i = indexOf(map, x, y, h.z);
      if (i < 0 || !isWaterCell(map, i)) continue;
      if (occupantAt(map, i) !== null) continue;
      // Ties break on cell index, so the choice is map order, not loop order.
      if (i < bestIndex) { bestIndex = i; best = [x, y, h.z]; }
    }
  }
  return best;
}

function nearestIdleBoat(c: Ctx, heroId: HeroId): ObjectId | null {
  const h = c.s.heroes[heroId]!;
  let best: ObjectId | null = null;
  let bestD = Number.MAX_SAFE_INTEGER;
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id as ObjectId]!;
    if (o.removed || o.kind !== "boat" || o.owner !== h.owner) continue;
    if (boatOccupied(c, o.id)) continue;
    const d = tileDistance(h.x, h.y, o.x, o.y) + (o.z === h.z ? 0 : OTHER_PLANE);
    if (d < bestD) { bestD = d; best = o.id; }
  }
  return best;
}

export function moveObject(c: Ctx, id: ObjectId, x: number, y: number, z: number): void {
  const o = c.s.objects[id];
  if (o === undefined) return;
  clearFootprint(c, o);
  patchObject(c, id, { x, y, z });
  layFootprint(c, c.s.objects[id]!);
}

function clearFootprint(c: Ctx, o: MapObjectState): void {
  const footprint = c.content.mapObjects.get(o.def)?.footprint ?? [];
  if (footprint.length === 0) {
    setOccupant(c.s.map, o.x, o.y, o.z, null, false);
    return;
  }
  for (const [dx, dy] of footprint) setOccupant(c.s.map, o.x + dx, o.y + dy, o.z, null, false);
}

/**
 * The smallest boat the content ships, by footprint then by id. A summoned hull
 * has one free water cell to stand in, so a two-cell barge would have to stamp
 * its second cell onto the shore.
 */
function boatDef(content: ContentRegistry): string | null {
  let best: string | null = null;
  let bestCells = Number.MAX_SAFE_INTEGER;
  for (const id of [...content.mapObjects.keys()].sort()) {
    const def = content.mapObjects.get(id)!;
    if (def.kind !== "boat") continue;
    const cells = def.footprint.length === 0 ? 1 : def.footprint.length;
    if (cells < bestCells) { bestCells = cells; best = id; }
  }
  return best;
}

// ---------------------------------------------------------- the scryings

/**
 * The view spells light the *objects* they name, not the ground between them:
 * one cell per matching object inside range, which is what puts each of them
 * into the caster's memory without handing over the country they stand in.
 */
function view(
  c: Ctx, heroId: HeroId, mastery: Mastery, kinds: ReadonlySet<string>,
): Reason | null {
  const h = c.s.heroes[heroId]!;
  const fog = c.s.fog[h.owner];
  if (fog === undefined) return { code: "TARGET_INVALID" };
  const range = VIEW_RANGE_BY_MASTERY[mastery] ?? VIEW_RANGE_BY_MASTERY[0]!;
  if (range < 0) {
    // Expert: "across the whole map". Explored, not lit - the caster learns the
    // ground, not what is standing on it right now.
    revealAll(fog, 1);
    refreshMemory(c, h.owner);
    return null;
  }
  revealObjects(c, heroId, range, (o) => kinds.has(o.kind));
  return null;
}

function visions(c: Ctx, heroId: HeroId, mastery: Mastery): Reason | null {
  const range = VISIONS_RANGE_BY_MASTERY[mastery] ?? VISIONS_RANGE_BY_MASTERY[0]!;
  revealObjects(c, heroId, range, (o) => o.kind === "monster" || o.guard !== null);
  return null;
}

function revealObjects(
  c: Ctx, heroId: HeroId, range: number, want: (o: MapObjectState) => boolean,
): void {
  const h = c.s.heroes[heroId]!;
  const fog = c.s.fog[h.owner];
  if (fog === undefined) return;
  const cells: number[] = [];
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id as ObjectId]!;
    if (o.removed || o.z !== h.z || !want(o)) continue;
    if (tileDistance(h.x, h.y, o.x, o.y) > range) continue;
    for (const i of revealAround(fog, o.x, o.y, o.z, 0, c.s.map)) cells.push(i);
  }
  if (cells.length > 0) {
    cells.sort((a, b) => a - b);
    c.events.push({ type: "FogRevealed", player: h.owner, cells });
  }
  refreshMemory(c, h.owner);
}

// -------------------------------------------------------------- disguise

/**
 * Disguise does not change a number on the caster; it changes the number
 * *other players read*. The mastery is carried by which of the four sources is
 * on the hero, so `disguisedLevel` in `hero.ts` - the one place that masks the
 * reading - needs no field on `HeroState` and no second stat.
 */
function disguise(c: Ctx, heroId: HeroId, mastery: Mastery): Reason | null {
  const h = c.s.heroes[heroId]!;
  const kept = h.modifiers.filter((m) => !SRC_DISGUISE_BY_MASTERY.includes(m.source));
  patchHero(c, heroId, {
    modifiers: [...kept, {
      source: SRC_DISGUISE_BY_MASTERY[mastery]!, stat: "movement", op: "add", value: 0,
      expires: { kind: "turns", turns: 1 },
    }],
  });
  return null;
}

// ------------------------------------------------------- the new castings
//
// Seven spells that act on the map rather than on a battlefield, added beside
// the classic ten. Each is the same three-part shape those use: a pure "may
// this be cast" that a targeting cursor can ask, a table of numbers indexed by
// mastery, and a body that mutates nothing before the last refusal.

/** Has this hero already spent the day's cast of a once-a-day spell? */
function castToday(h: HeroState, src: ModifierSrcId): boolean {
  for (const m of h.modifiers) if (m.source === src) return true;
  return false;
}

/** Spend the day's cast. The marker expires with the turn, as Disguise's does. */
function markToday(c: Ctx, heroId: HeroId, src: ModifierSrcId): void {
  const h = c.s.heroes[heroId]!;
  patchHero(c, heroId, {
    modifiers: [...h.modifiers, {
      source: src, stat: "movement", op: "add", value: 0,
      expires: { kind: "turns", turns: 1 },
    }],
  });
}

// ------------------------------------------------------------------ recall

/**
 * The hero this cast would pull: the caster's nearest own hero inside reach.
 *
 * Nearest rather than chosen, for the same reason Homeward Gate sends a hero to
 * the nearest town below expert - naming one would need a second target shape
 * and a hero picker, and the spell is about closing a distance, so the distance
 * is what decides.
 */
export function recallTarget(c: Ctx, h: HeroState, mastery: Mastery): HeroId | null {
  const range = RECALL_RANGE_BY_MASTERY[mastery] ?? RECALL_RANGE_BY_MASTERY[0]!;
  if (recallLanding(c, h) === null) return null;
  let best: HeroId | null = null;
  let bestD = Number.MAX_SAFE_INTEGER;
  for (const id of entityKeys(c.s.heroes)) {
    const other = c.s.heroes[id as HeroId]!;
    if (other.id === h.id || other.owner !== h.owner) continue;
    // Not out of a boat: the hull would be left crewless in open water and the
    // hero would arrive standing on the sea.
    if (other.boat !== null) continue;
    if (other.z !== h.z) continue;
    const d = tileDistance(h.x, h.y, other.x, other.y);
    if (d > range || d >= bestD) continue;
    bestD = d; best = other.id;
  }
  return best;
}

/** The cell a recalled hero lands on: the free neighbour of lowest map index. */
function recallLanding(c: Ctx, h: HeroState): readonly [number, number, number] | null {
  const rules = moveRulesFor(c, h);
  let best: readonly [number, number, number] | null = null;
  let bestIndex = Number.MAX_SAFE_INTEGER;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const x = h.x + dx, y = h.y + dy;
      const i = indexOf(c.s.map, x, y, h.z);
      if (i < 0 || !cellEnterable(c.s.map, i, rules)) continue;
      if (occupantAt(c.s.map, i) !== null) continue;
      if (heroAt(c, x, y, h.z) !== null) continue;
      if (i < bestIndex) { bestIndex = i; best = [x, y, h.z]; }
    }
  }
  return best;
}

/**
 * Recall. The inverse of Homeward Gate: instead of sending the caster to his
 * kingdom, it brings his kingdom to him.
 *
 * The recalled hero pays for it, not the caster - the day's travel is spent by
 * whoever was moved, which is what stops a chain of heroes from teleporting a
 * fresh army to the front every morning for one hero's mana.
 */
function recall(c: Ctx, heroId: HeroId, mastery: Mastery): Reason | null {
  const h = c.s.heroes[heroId]!;
  const target = recallTarget(c, h, mastery);
  if (target === null) return { code: "TARGET_INVALID" };
  const at = recallLanding(c, h);
  if (at === null) return { code: "TARGET_INVALID" };
  leaveTown(c, target);
  patchHero(c, target, { x: at[0], y: at[1], z: at[2], inTown: null, movementLeft: 0 });
  revealStep(c, c.s.heroes[target]!);
  return null;
}

// -------------------------------------------------------------------- scry

/**
 * Scry. Where the other side's heroes are, and - because the fog lifts around
 * them rather than over them - what they are standing next to.
 *
 * Deliberately not a permanent reveal: `revealAround` at radius one lights the
 * hero's own ground, and the memory refresh copies it. Next turn it is a
 * memory, which is exactly what scrying should leave.
 */
/**
 * The enemy heroes this cast would find.
 *
 * The enumerator and the rule both ask *this*, and not two copies of the same
 * loop: a spell that is offered on a map with nobody to scry and then refused
 * when it is cast is an AI issuing a command the kernel rejects, which is the
 * one thing the legality contract promises cannot happen (I9, I10).
 */
export function scryTargets(c: Ctx, h: HeroState, mastery: Mastery): readonly HeroId[] {
  const range = SCRY_RANGE_BY_MASTERY[mastery] ?? SCRY_RANGE_BY_MASTERY[0]!;
  const out: HeroId[] = [];
  for (const id of entityKeys(c.s.heroes)) {
    const other = c.s.heroes[id as HeroId]!;
    if (other.owner === h.owner) continue;
    if (range >= 0 && (other.z !== h.z || tileDistance(h.x, h.y, other.x, other.y) > range)) continue;
    out.push(other.id);
  }
  return out;
}

function scry(c: Ctx, heroId: HeroId, mastery: Mastery): Reason | null {
  const h = c.s.heroes[heroId]!;
  const fog = c.s.fog[h.owner];
  if (fog === undefined) return { code: "TARGET_INVALID" };
  const found = scryTargets(c, h, mastery);
  if (found.length === 0) return { code: "TARGET_INVALID" };
  const cells: number[] = [];
  for (const id of found) {
    const other = c.s.heroes[id]!;
    for (const i of revealAround(fog, other.x, other.y, other.z, 1, c.s.map)) cells.push(i);
  }
  if (cells.length > 0) {
    cells.sort((a, b) => a - b);
    c.events.push({ type: "FogRevealed", player: h.owner, cells });
  }
  refreshMemory(c, h.owner);
  return null;
}

// ------------------------------------------------------------ blood ritual

/**
 * The stack this ritual would take from: the lowest-tier one with a creature
 * to spare.
 *
 * "To spare" is the whole rule - the offering never empties a slot, so a hero
 * cannot ritual his army away to nothing and cannot use it to drop an unwanted
 * stack. Lowest tier first because the ritual pays by tier: it is a way to
 * spend peasants, not champions.
 */
export function bloodRitualSlot(c: Ctx, h: HeroState, mastery: Mastery): number | null {
  const army = c.s.armies[h.army];
  if (army === undefined) return null;
  const want = BLOOD_RITUAL_CREATURES[mastery] ?? BLOOD_RITUAL_CREATURES[0]!;
  let best: number | null = null;
  let bestTier = Number.MAX_SAFE_INTEGER;
  for (let i = 0; i < army.slots.length; i++) {
    const slot = army.slots[i];
    if (slot === null || slot === undefined) continue;
    if (slot.count <= want) continue;
    const tier = c.content.creatures.get(slot.def)?.tier ?? 1;
    if (tier < bestTier) { bestTier = tier; best = i; }
  }
  return best;
}

/**
 * Blood Rite. Creatures for spell points, on the spot.
 *
 * The exchange is by tier rather than by head count, so the ritual is worth
 * taking with a stack of peasants and never worth taking with a stack of
 * dragons - which is the decision it exists to pose. Capped at the hero's own
 * pool, because mana above the maximum is not storable anywhere.
 */
function bloodRitual(c: Ctx, heroId: HeroId, mastery: Mastery): Reason | null {
  const h = c.s.heroes[heroId]!;
  if (castToday(h, SRC_BLOOD_RITUAL)) return { code: "TARGET_INVALID" };
  const index = bloodRitualSlot(c, h, mastery);
  if (index === null) return { code: "TARGET_INVALID" };
  const army = c.s.armies[h.army]!;
  const slot = army.slots[index]!;
  const offered = BLOOD_RITUAL_CREATURES[mastery] ?? BLOOD_RITUAL_CREATURES[0]!;
  const tier = c.content.creatures.get(slot.def)?.tier ?? 1;

  setSlot(c, h.army, index, { ...slot, count: slot.count - offered });
  const gained = offered * tier * BLOOD_RITUAL_MANA_PER_TIER;
  const cap = maxMana(c, h);
  const now = c.s.heroes[heroId]!;
  patchHero(c, heroId, { mana: Math.min(cap, now.mana + gained) });
  markToday(c, heroId, SRC_BLOOD_RITUAL);
  return null;
}

// ----------------------------------------------------------------- harvest

/**
 * Harvest. A day's extra yield out of the ground the hero is standing on.
 *
 * Paid straight into the treasury rather than modelled as a mine that produces
 * twice tomorrow: a doubled mine would need a per-mine flag and a second place
 * for the day boundary to read it, and the player cannot tell the difference
 * between the two except that this one arrives when it was cast.
 */
function harvest(c: Ctx, heroId: HeroId, mastery: Mastery): Reason | null {
  const h = c.s.heroes[heroId]!;
  if (castToday(h, SRC_SPELL_HARVEST)) return { code: "TARGET_INVALID" };
  const bulk = HARVEST_BY_MASTERY[mastery] ?? HARVEST_BY_MASTERY[0]!;
  addResource(c, h.owner, "wood", bulk);
  addResource(c, h.owner, "ore", bulk);
  if (mastery >= 3) {
    for (const r of RARE_RESOURCES) addResource(c, h.owner, r, HARVEST_RARE_AT_EXPERT);
  }
  markToday(c, heroId, SRC_SPELL_HARVEST);
  return null;
}

/** The four resources a mine yields one of a day. Gold and the bulk two apart. */
const RARE_RESOURCES: readonly ResourceKind[] =
  Object.freeze(["mercury", "sulfur", "crystal", "gems"]);

// --------------------------------------------------------------- transmute

/**
 * What this cast would melt down: the most plentiful resource that is not gold.
 *
 * Chosen rather than asked for, so the spell needs no second target shape.
 * Ties break on `RESOURCES` order, which makes the choice a property of the
 * treasury and not of iteration (I3).
 */
export function transmuteSource(c: Ctx, h: HeroState): ResourceKind | null {
  const pl = c.s.players[h.owner];
  if (pl === undefined) return null;
  let best: ResourceKind | null = null;
  let bestHave = TRANSMUTE_UNITS - 1;
  for (const r of RESOURCES) {
    if (r === "gold") continue;
    const have = pl.resources[r];
    if (have > bestHave) { bestHave = have; best = r; }
  }
  return best;
}

/**
 * Transmute. Raw materials into coin, at a rate the market will not give you.
 *
 * The market's own rate falls as you sell, which is what makes a stockpile of
 * ore hard to turn into an army; this is flat, once a day, and small enough
 * that it is a trickle rather than a mint.
 */
function transmute(c: Ctx, heroId: HeroId, mastery: Mastery): Reason | null {
  const h = c.s.heroes[heroId]!;
  if (castToday(h, SRC_TRANSMUTE)) return { code: "TARGET_INVALID" };
  const from = transmuteSource(c, h);
  if (from === null) return { code: "TARGET_INVALID" };
  const rate = TRANSMUTE_GOLD_PER_UNIT[mastery] ?? TRANSMUTE_GOLD_PER_UNIT[0]!;
  addResource(c, h.owner, from, -TRANSMUTE_UNITS);
  addResource(c, h.owner, "gold", TRANSMUTE_UNITS * rate);
  markToday(c, heroId, SRC_TRANSMUTE);
  return null;
}

// ----------------------------------------------------------- meteor strike

/**
 * Meteor Strike. Thinning a guard before anybody walks into it.
 *
 * The one spell in the game that reaches into a fight that has not started.
 * What it destroys is counted in hit points and taken off whole creatures, so
 * a strike that cannot kill even one of what it hits does nothing at all -
 * which is what keeps it from chipping a dragon to death over a week. Once a
 * day, and never the last creature: a guard reduced to nothing would leave an
 * object standing open with a guard army of zero, and every rule that asks
 * "is this guarded" would have to learn a second answer.
 */
export interface MeteorPlan {
  readonly object: ObjectId;
  readonly army: ArmyId;
  /** Slot index and how many creatures in it the stone destroys. */
  readonly kills: readonly (readonly [number, number])[];
}

/**
 * What a strike at this cell would destroy, or the reason it may not be made.
 *
 * Worked out in full before anything is mutated, because the answer decides
 * whether the cast is *offered* as well as whether it lands: a strike too weak
 * to kill even one creature is refused, and a refusal the enumerator could not
 * predict is a command the AI would have issued and the kernel would have
 * thrown out (I10).
 */
export function meteorStrikeTarget(
  c: Ctx, h: HeroState, mastery: Mastery, x: number, y: number, z: number,
): Result<MeteorPlan, Reason> {
  if (castToday(h, SRC_METEOR_STRIKE)) return Err({ code: "TARGET_INVALID" });
  if (z !== h.z) return Err({ code: "TARGET_INVALID" });
  const range = METEOR_RANGE_BY_MASTERY[mastery] ?? METEOR_RANGE_BY_MASTERY[0]!;
  const distance = tileDistance(h.x, h.y, x, y);
  if (distance > range) return Err({ code: "OUT_OF_RANGE", distance, max: range });

  const i = indexOf(c.s.map, x, y, z);
  if (i < 0) return Err({ code: "TARGET_INVALID" });
  const occ = occupantAt(c.s.map, i);
  const o = occ === null ? undefined : c.s.objects[occ];
  if (o === undefined || o.removed || o.guard === null) return Err({ code: "TARGET_INVALID" });
  const guard = c.s.armies[o.guard];
  if (guard === undefined) return Err({ code: "TARGET_INVALID" });

  const power = statFor(c, h, "spellPower", h.primary.spellPower).value;
  let left = (METEOR_HP_PER_POWER[mastery] ?? METEOR_HP_PER_POWER[0]!) * power;
  // Strongest slot first, so the strike bites into what actually guards the
  // object rather than shaving the escort.
  const order: number[] = [];
  for (let k = 0; k < guard.slots.length; k++) if (guard.slots[k] != null) order.push(k);
  order.sort((a, b) => {
    const ta = c.content.creatures.get(guard.slots[a]!.def)?.tier ?? 0;
    const tb = c.content.creatures.get(guard.slots[b]!.def)?.tier ?? 0;
    return tb - ta || a - b;
  });

  const kills: (readonly [number, number])[] = [];
  let spared = false;
  for (const k of order) {
    const slot = guard.slots[k]!;
    const hp = c.content.creatures.get(slot.def)?.hp ?? 1;
    // Never the last creature of the guard: an object standing open with an
    // empty guard army would make "is this guarded" a question with two
    // answers. One head is left in the slot the strike opens on.
    const room = slot.count - (spared ? 0 : 1);
    const canKill = Math.min(room, Math.floor(left / hp));
    if (canKill <= 0) continue;
    kills.push([k, canKill]);
    left -= canKill * hp;
    spared = true;
  }
  if (kills.length === 0) return Err({ code: "TARGET_INVALID" });
  return Ok({ object: o.id, army: o.guard, kills });
}

function meteorStrike(
  c: Ctx, heroId: HeroId, mastery: Mastery, x: number, y: number, z: number,
): Reason | null {
  const h = c.s.heroes[heroId]!;
  const plan = meteorStrikeTarget(c, h, mastery, x, y, z);
  if (!plan.ok) return plan.error;
  const guard = c.s.armies[plan.value.army];
  if (guard === undefined) return { code: "TARGET_INVALID" };
  for (const [k, n] of plan.value.kills) {
    const slot = guard.slots[k];
    if (slot === null || slot === undefined) continue;
    setSlot(c, plan.value.army, k, { ...slot, count: slot.count - n });
  }
  markToday(c, heroId, SRC_METEOR_STRIKE);
  return null;
}

// ------------------------------------------------------------------ tunnel

/** The cell directly below or above the hero, if it can be stood on. */
export function tunnelTarget(c: Ctx, h: HeroState): readonly [number, number, number] | null {
  if (c.s.map.levels < 2) return null;
  if (h.boat !== null || h.movementLeft < TUNNEL_MOVEMENT_COST) return null;
  const z = h.z === 0 ? 1 : 0;
  const i = indexOf(c.s.map, h.x, h.y, z);
  if (i < 0) return null;
  if (!cellEnterable(c.s.map, i, moveRulesFor(c, h))) return null;
  if (occupantAt(c.s.map, i) !== null) return null;
  if (heroAt(c, h.x, h.y, z) !== null) return null;
  return [h.x, h.y, z];
}

/**
 * Tunnel. Straight down, or straight up, without finding a gate first.
 *
 * The plane change every subterranean gate offers, at a price: three plain
 * steps of the day's travel, the same as a Blink Step crossing. That is
 * what keeps the two-level map a puzzle - the gates are still the cheap way
 * through, and this is the way through when the cheap way is held against you.
 */
function tunnel(c: Ctx, heroId: HeroId, mastery: Mastery): Reason | null {
  void mastery;
  const h = c.s.heroes[heroId]!;
  const at = tunnelTarget(c, h);
  if (at === null) return { code: "TARGET_INVALID" };
  leaveTown(c, heroId);
  patchHero(c, heroId, {
    x: at[0], y: at[1], z: at[2], inTown: null,
    movementLeft: h.movementLeft - TUNNEL_MOVEMENT_COST,
  });
  revealStep(c, c.s.heroes[heroId]!);
  return null;
}

// ----------------------------------------------------------------- misc

function heroAt(c: Ctx, x: number, y: number, z: number): HeroId | null {
  for (const id of entityKeys(c.s.heroes)) {
    const h = c.s.heroes[id as HeroId]!;
    if (h.x === x && h.y === y && h.z === z) return h.id;
  }
  return null;
}

/** Release the town a hero is standing in before moving them out of it. */
function leaveTown(c: Ctx, heroId: HeroId): void {
  const h = c.s.heroes[heroId]!;
  if (h.inTown === null) return;
  const t = c.s.towns[h.inTown];
  if (t !== undefined && t.visitingHero === heroId) {
    patchTown(c, h.inTown, { visitingHero: null });
  }
}
